import EditOutlined from '@mui/icons-material/EditOutlined';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { AiReviewStatus, BillingLineSide } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { MouseEvent, ReactNode, useCallback, useContext, useState } from 'react';

import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { Job, JobInvoicing } from '~hooks/useJob/models';
import JobCardPdfTypeDialog from '~hooks/useJobCardPdf/JobCardPdfTypeDialog';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { useJobCardPdf } from '~hooks/useJobCardPdf/useJobCardPdf';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import ActionsColumn from '~pages/Approvals/Files/components/JobsTable/Columns/ActionsColumn';
import DriverAndTruckColumn from '~pages/Approvals/Files/components/JobsTable/Columns/DriverAndTruckColumn';
import TimeAndDurationColumn from '~pages/Approvals/Files/components/JobsTable/Columns/TimeAndDurationColumn';
import TotalQuantityColumn from '~pages/Approvals/Files/components/JobsTable/Columns/TotalQuantityColumn';
import JobsQueryContext from '~pages/Approvals/Files/components/OrdersList/ExpandedBlock/JobsQueryContext';
import { GeofenceStartAdornment } from '~pages/Settings/Administration/Sites/components/GeofenceStartAdornment';
import { extractWaypointData } from '~utils/orders';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { ChargesGrainTab } from '../approveChargesTabs';
import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import {
  CHARGES_CELL_SX,
  CHARGES_GRID_TEMPLATE_COLUMNS,
  chargesGridColumn,
  chargesRowTextSx,
  NO_VALUE_PLACEHOLDER,
} from '../chargesGrid';
import {
  getPerLoadLinesSettledByUnit,
  getTimeBasedLines,
  JobCharges,
} from '../groupBillingLines';
import ChargeAmountCell from './ChargeAmountCell';
import ChargeLinesCheckbox from './ChargeLinesCheckbox';
import ChargesInvoiceEditDialog from './ChargesInvoiceEditDialog';
import ChargesTruckCell from './ChargesTruckCell';
import SettlementNumberChip from './SettlementNumberChip';

function getSideAmount(side?: JobCharges['payable']) {
  if (!side?.hasLines) {
    return null;
  }

  return side.isRejected ? side.rejectedTotal : side.total;
}

const INTERACTIVE_ELEMENT_SELECTOR =
  'a, button, input, label, [role="button"], [role="checkbox"], [role="menu"], [role="menuitem"]';

const editInvoiceLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.actions.edit_payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.actions.edit_receivable',
} satisfies Record<BillingLineSide, string>;

const JOB_ROW_MIN_HEIGHT = '88px';

const JOB_CELL_SX = { ...CHARGES_CELL_SX, py: 1.75 } as const;

const AI_REVIEW_STATUSES_NEEDING_A_LOOK: ReadonlySet<string> = new Set([
  AiReviewStatus.FLAGGED,
  AiReviewStatus.UNREADABLE,
]);

interface ChargesJobRowProps {
  charges?: JobCharges;
  isOpen: boolean;
  job: JobInvoicing;
  loads?: Load[];
  onToggle: () => void;
  primaryAction?: ReactNode;
  tickets?: Ticket[];
}

export default function ChargesJobRow({
  charges,
  isOpen,
  job,
  loads,
  onToggle,
  primaryAction,
  tickets,
}: ChargesJobRowProps) {
  const { updateJobInQueryData } = useContext(JobsQueryContext);
  const { reload } = useContext(ChargesBillingLinesQueryContext);
  const { grainTab } = useContext(ApproveChargesPageContext);
  const isHourlyTab = grainTab === ChargesGrainTab.HOURLY_JOBS;
  const getJobCheckboxLines = isHourlyTab
    ? getTimeBasedLines
    : getPerLoadLinesSettledByUnit;
  const { generateJobCardPdf, generatingJobIds } = useJobCardPdf();
  const [pdfDialogJob, setPdfDialogJob] = useState<Job | null>(null);
  const [editedInvoiceSide, setEditedInvoiceSide] = useState<BillingLineSide | null>(
    null,
  );
  const editInvoiceActions = [BillingLineSide.PAYABLE, BillingLineSide.RECEIVABLE]
    .filter((side) => charges?.[side].hasLines)
    .map((side) => ({
      icon: EditOutlined,
      key: `edit-${side}`,
      label: t(editInvoiceLabelKeys[side]),
      onSelect: () => setEditedInvoiceSide(side),
    }));
  const jobStart = dayjs.tz(job.jobStartAt);
  const sites = [
    extractWaypointData(job.pickUpWayPoints[0]),
    extractWaypointData(job.dropOffWayPoints[0]),
  ];
  const completedLoadsCount =
    job.jobSummary?.completedLoadsCount ?? job.completedLoadsCount;
  const orderName = job.order?.name || job.order?.orderId || NO_VALUE_PLACEHOLDER;
  const customerName = job.order?.account?.name ?? NO_VALUE_PLACEHOLDER;
  const approvedLoadsCount = loads?.filter((load) => load.billingApprovedAt).length;

  const activeTickets = (tickets ?? []).filter((ticket) => !ticket.discardedAt);
  const ticketIssueCounts = tickets
    ? [
        {
          count: (loads ?? []).filter(
            (load) =>
              load.aiReview &&
              !load.aiReview.dismissedAt &&
              AI_REVIEW_STATUSES_NEEDING_A_LOOK.has(String(load.aiReview.status)),
          ).length,
          labelKey: 'approvals.approve_charges.ticket_issues.ai_flagged',
        },
        {
          count: Math.max(
            0,
            (job.jobSummary?.completedLoadsCount ?? 0) -
              (job.jobSummary?.loadsWithTicketsCount ?? 0),
          ),
          labelKey: 'approvals.approve_charges.ticket_issues.missing_tickets',
        },
        {
          count: activeTickets.filter((ticket) => ticket.isPotentialDuplicateFlagged)
            .length,
          labelKey: 'approvals.approve_charges.ticket_issues.possible_duplicates',
        },
        {
          count: activeTickets.filter((ticket) => !(Number(ticket.quantity) > 0)).length,
          labelKey: 'approvals.approve_charges.ticket_issues.tickets_without_quantity',
        },
        {
          count: activeTickets.filter((ticket) => ticket.flagged).length,
          labelKey: 'approvals.approve_charges.ticket_issues.user_flagged',
        },
      ]
    : [];
  const ticketIssues = ticketIssueCounts
    .filter(({ count }) => count > 0)
    .map(({ count, labelKey }) => t(labelKey, { count }));

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as Element;
      const isOwnControl = Boolean(target.closest(INTERACTIVE_ELEMENT_SELECTOR));
      const isInRow = event.currentTarget.contains(target);

      if (isInRow && !isOwnControl) {
        onToggle();
      }
    },
    [onToggle],
  );

  const handleJobUpdated = useCallback(
    (updatedJob: JobInvoicing) => {
      updateJobInQueryData(updatedJob);
      reload();
    },
    [reload, updateJobInQueryData],
  );

  const handleViewJobCardPdf = useCallback((jobToView: Job) => {
    setPdfDialogJob(jobToView);

    return Promise.resolve();
  }, []);

  const handlePdfTypeSelect = useCallback(
    (recipientType: JobCardPdfRecipientType) => {
      if (pdfDialogJob) {
        generateJobCardPdf(pdfDialogJob, recipientType);
      }

      setPdfDialogJob(null);
    },
    [generateJobCardPdf, pdfDialogJob],
  );

  return (
    <>
      <Box
        data-test-id="charges-job-row"
        onClick={handleRowClick}
        sx={(theme) => ({
          alignItems: 'start',
          bgcolor: theme.semantic.surfaceRaised,
          borderBottom: `solid 1px ${
            isOpen ? theme.brandV2.colors.treadGray7 : theme.semantic.divider
          }`,
          cursor: 'pointer',
          display: 'grid',
          gridAutoRows: `minmax(${JOB_ROW_MIN_HEIGHT}, auto)`,
          gridTemplateColumns: CHARGES_GRID_TEMPLATE_COLUMNS,
          ...chargesRowTextSx(theme),
          '&:hover': {
            bgcolor: isOpen
              ? theme.semantic.surfaceRaised
              : theme.brandV2.colors.treadGray9,
          },
        })}
      >
        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.select }}>
          {charges && (
            <ChargeLinesCheckbox
              label={t('approvals.approve_charges.selection.job')}
              lines={getJobCheckboxLines(charges)}
            />
          )}
        </Box>

        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.date }}>
          <OverflowAwareText sx={{ fontWeight: 700 }}>
            {jobStart.format('MMM D')}
          </OverflowAwareText>
          <OverflowAwareText>{jobStart.format('ddd')}</OverflowAwareText>
        </Box>

        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.order }}>
          <OverflowAwareText sx={{ fontWeight: 700 }} title={orderName}>
            {orderName}
          </OverflowAwareText>
          <OverflowAwareText title={job.order?.orderId}>
            {job.order?.orderId}
          </OverflowAwareText>
          {job.order?.prevailingWage && (
            <Box sx={{ mt: 0.5 }}>
              <PrevailingWageBadge compact />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            ...JOB_CELL_SX,
            gridColumn: chargesGridColumn.customerAndProject,
          }}
        >
          <OverflowAwareText sx={{ fontWeight: 600 }} title={customerName}>
            {customerName}
          </OverflowAwareText>
          <OverflowAwareText title={job.projectName}>{job.projectName}</OverflowAwareText>
        </Box>

        <DriverAndTruckColumn
          hideEquipment
          isJobEquipmentOnly
          job={job}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.driverAndVendor }}
        />

        <ChargesTruckCell
          job={job}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.truck }}
        />

        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.route }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {sites.map((site, index) => (
              <Box key={index} sx={{ display: 'flex' }}>
                <GeofenceStartAdornment
                  geofenceType={site?.geofenceType}
                  sx={{ pr: 0.5 }}
                />
                <OverflowAwareText title={site?.name}>
                  {site?.name || NO_VALUE_PLACEHOLDER}
                </OverflowAwareText>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.material }}>
          <OverflowAwareText title={job.material?.name}>
            {job.material?.name ?? NO_VALUE_PLACEHOLDER}
          </OverflowAwareText>
        </Box>

        {isHourlyTab && (
          <TimeAndDurationColumn
            job={job}
            sx={{
              ...JOB_CELL_SX,
              gridColumn: chargesGridColumn.timeAndDuration,
            }}
          />
        )}

        <Box sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.loads }}>
          <OverflowAwareText sx={{ fontWeight: 700 }}>
            {`${completedLoadsCount} ${t('common.completed')}`}
          </OverflowAwareText>
          {approvedLoadsCount !== undefined && (
            <OverflowAwareText>
              {t('approvals.approve_charges.approved_loads', {
                count: approvedLoadsCount,
              })}
            </OverflowAwareText>
          )}
        </Box>

        <TotalQuantityColumn
          job={job}
          quantityAdornment={
            ticketIssues.length > 0 ? (
              <Tooltip
                title={
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {ticketIssues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </Box>
                }
              >
                <WarningAmber
                  aria-label={t('approvals.approve_charges.ticket_issues.label')}
                  data-test-id="job-ticket-issues-icon"
                  sx={(theme) => ({
                    color: theme.brandV2.colors.treadOrange,
                    fontSize: 18,
                  })}
                />
              </Tooltip>
            ) : null
          }
          sx={{
            ...JOB_CELL_SX,
            gridColumn: chargesGridColumn.ticketQuantity,
          }}
        />

        <ChargeAmountCell
          addOnsAmount={charges?.payable.addOnsTotal}
          amount={getSideAmount(charges?.payable)}
          isRejected={charges?.payable.isRejected}
          rate={charges?.payable.freight?.rate}
          rateType={charges?.payable.freight?.rateType}
          rejectedReason={charges?.payable.freight?.rejectedReason}
          wasRate={charges?.payable.freight?.wasRate}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.payableAmount }}
        />
        <ChargeAmountCell
          addOnsAmount={charges?.receivable.addOnsTotal}
          amount={getSideAmount(charges?.receivable)}
          isRejected={charges?.receivable.isRejected}
          rate={charges?.receivable.freight?.rate}
          rateType={charges?.receivable.freight?.rateType}
          rejectedReason={charges?.receivable.freight?.rejectedReason}
          wasRate={charges?.receivable.freight?.wasRate}
          sx={{
            ...JOB_CELL_SX,
            gridColumn: chargesGridColumn.receivableAmount,
          }}
        />

        <SettlementNumberChip
          hasUnrejectedLines={charges?.payable.settlement?.isRejected === false}
          hasUnsettledLines={charges?.payable.settlement?.hasUnsettledLines}
          isApproved={charges?.payable.settlement?.isSettlementApproved}
          settlementNumbers={charges?.payable.settlement?.settlementNumbers ?? []}
          side={BillingLineSide.PAYABLE}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.payableNumber }}
        />
        <SettlementNumberChip
          hasUnrejectedLines={charges?.receivable.settlement?.isRejected === false}
          hasUnsettledLines={charges?.receivable.settlement?.hasUnsettledLines}
          isApproved={charges?.receivable.settlement?.isSettlementApproved}
          settlementNumbers={charges?.receivable.settlement?.settlementNumbers ?? []}
          side={BillingLineSide.RECEIVABLE}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.invoiceNumber }}
        />

        <ActionsColumn
          isGeneratingJobCardPdf={generatingJobIds.has(job.id)}
          job={job}
          menuActions={editInvoiceActions}
          onJobUpdated={handleJobUpdated}
          onViewJobCardPdf={handleViewJobCardPdf}
          primaryAction={primaryAction}
          sx={{ ...JOB_CELL_SX, gridColumn: chargesGridColumn.status }}
        />
      </Box>

      {editedInvoiceSide && (
        <ChargesInvoiceEditDialog
          jobId={job.id}
          onClose={() => {
            setEditedInvoiceSide(null);
            reload();
          }}
          side={editedInvoiceSide}
        />
      )}

      <JobCardPdfTypeDialog
        isOpen={pdfDialogJob !== null}
        onClose={() => setPdfDialogJob(null)}
        onSelect={handlePdfTypeSelect}
      />
    </>
  );
}
